/**
 * Safe localStorage helpers — thin try/catch wrappers around the standard API.
 *
 * For writing: use `safeLocalStorageWrite` for atomic overwrite (most cases).
 * Use `safeLocalStorageMerge` only when updating one property within a shared
 * JSON object (e.g., user preferences keyed by a single localStorage key).
 */

export function getUserStorageKey(userId: string): string {
  return `moveo_${userId || 'unknown'}`;
}

export function safeLocalStorageRead(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.warn('Failed to read from localStorage:', error);
    return null;
  }
}

export function safeLocalStorageWrite(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch (error) {
    console.warn('Failed to write to localStorage:', error);
  }
}

export function safeLocalStorageMerge(key: string, value: string): void {
  const existingValue = localStorage.getItem(key);
  let mergedValue = value;

  try {
    const existingObject = JSON.parse(existingValue);
    const newObject = JSON.parse(value);
    mergedValue = JSON.stringify({ ...existingObject, ...newObject });

    localStorage.setItem(key, mergedValue);
  } catch (error) {
    console.warn('Failed to write to localStorage:', error);
  }
}

export function safeLocalStorageRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.warn('Failed to remove from localStorage:', error);
  }
}

export function safeLocalStorageParse<T>(raw: string | null): T | null {
  if (raw === null) {
    return null;
  }
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}
